import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Copy, Mail } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';

import { api, ensureOk, unwrap } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import { PermissionGate } from '@/auth/PermissionGate';
import { useCan, useMe } from '@/auth/queries';
import { useStepUp } from '@/auth/StepUp';
import { Campo } from '@/components/Campo';
import { ConfirmarAccion } from '@/components/ConfirmarAccion';
import { Cargando, Encabezado, ErrorCarga } from '@/components/Estados';
import { fechaHora } from '@/lib/formato';

import { EditorSecciones } from './EditorSecciones';
import {
  useCatalogoSecciones,
  useStaff,
  useStaffLista,
  type SeccionInput,
  type StaffDetalle,
} from './queries';

const schema = z.object({
  name: z.string().trim().min(2, 'Escribe el nombre').max(100),
  email: z.string().trim().email('Escribe un correo válido').max(120),
  startUrl: z
    .string()
    .trim()
    .max(250)
    .regex(/^(\/[A-Za-z0-9/_\-?=&.]*)?$/, 'Debe ser una ruta del panel, por ejemplo /alumnos'),
  isSuperadmin: z.boolean(),
});
type Form = z.infer<typeof schema>;

function seccionesDe(d: StaffDetalle): SeccionInput[] {
  return d.sections
    .filter((s) => s.level !== null)
    .map((s) => ({ section: s.section, level: s.level as 'ver' | 'editar' }));
}

function Formulario({ actual }: { actual: StaffDetalle | null }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: me } = useMe();
  const puedeEditar = useCan('staff', 'editar');
  const { proteger, dialogo } = useStepUp();
  const catalogo = useCatalogoSecciones();
  const otros = useStaffLista({ page: 1, status: 'active' });
  const [secciones, setSecciones] = useState<SeccionInput[]>(actual ? seccionesDe(actual) : []);
  const [confirmarBaja, setConfirmarBaja] = useState(false);
  const esYo = actual !== null && actual.id === me?.id;
  const bloqueado = !puedeEditar || (actual?.isSuperadmin === true && !me?.isSuperadmin);

  const form = useForm<Form>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: actual?.name ?? '',
      email: actual?.email ?? '',
      startUrl: actual?.startUrl ?? '',
      isSuperadmin: actual?.isSuperadmin ?? false,
    },
  });
  const { errors } = form.formState;

  const refrescar = (d: StaffDetalle) => {
    qc.setQueryData(['staff', d.id], d);
    void qc.invalidateQueries({ queryKey: ['staff', 'lista'] });
  };

  const guardar = useMutation({
    mutationFn: (v: Form) =>
      proteger(() =>
        actual
          ? unwrap<StaffDetalle>(
              api.PATCH('/api/v1/admin/staff-users/{id}', {
                params: { path: { id: String(actual.id) } },
                body: {
                  name: v.name,
                  startUrl: v.startUrl || null,
                  ...(!esYo && me?.isSuperadmin && { isSuperadmin: v.isSuperadmin }),
                },
              }),
            )
          : unwrap<StaffDetalle>(
              api.POST('/api/v1/admin/staff-users', {
                body: {
                  name: v.name,
                  email: v.email,
                  startUrl: v.startUrl || null,
                  isSuperadmin: v.isSuperadmin,
                  sections: secciones,
                },
              }),
            ),
      ),
    onSuccess: (d) => {
      refrescar(d);
      if (actual) toast.success('Cambios guardados.');
      else {
        toast.success(`Enviamos la invitación a ${d.email}.`);
        void navigate(`/staff/${d.id}`, { replace: true });
      }
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });

  const guardarSecciones = useMutation({
    mutationFn: (body: { sections: SeccionInput[] } | { copyFrom: number }) =>
      proteger(() =>
        unwrap<StaffDetalle>(
          api.PUT('/api/v1/admin/staff-users/{id}/sections', {
            params: { path: { id: String(actual?.id) } },
            body,
          }),
        ),
      ),
    onSuccess: (d) => {
      refrescar(d);
      setSecciones(seccionesDe(d));
      toast.success('Secciones actualizadas.');
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });

  const cambiarEstado = useMutation({
    mutationFn: (status: 'active' | 'disabled') =>
      proteger(() =>
        unwrap<StaffDetalle>(
          api.PATCH('/api/v1/admin/staff-users/{id}', {
            params: { path: { id: String(actual?.id) } },
            body: { status },
          }),
        ),
      ),
    onSuccess: (d) => {
      refrescar(d);
      setConfirmarBaja(false);
      toast.success(d.status === 'active' ? 'Cuenta reactivada.' : 'Cuenta desactivada.');
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });

  const invitar = useMutation({
    mutationFn: () =>
      ensureOk(
        api.POST('/api/v1/admin/staff-users/{id}/invite', {
          params: { path: { id: String(actual?.id) } },
        }),
      ),
    onSuccess: () => toast.success('Enviamos el enlace por correo.'),
    onError: (e) => toast.error(mensajeDe(e)),
  });

  return (
    <>
      <Link to="/staff" className="mb-3 inline-flex items-center gap-1 text-sm font-semibold">
        <ArrowLeft size={16} aria-hidden /> Staff
      </Link>
      <Encabezado
        titulo={actual ? actual.name : 'Nuevo staff'}
        descripcion={
          actual
            ? `${actual.email} · último acceso ${fechaHora(actual.lastLoginAt)} · ${actual.activeSessions} sesión(es) abierta(s)`
            : 'La cuenta se crea sin contraseña: le enviamos un enlace para definirla.'
        }
        acciones={
          actual &&
          !bloqueado &&
          !esYo && (
            <>
              {actual.status === 'active' && (
                <button
                  type="button"
                  className="btn-cs btn-contorno"
                  disabled={invitar.isPending}
                  onClick={() => invitar.mutate()}
                >
                  <Mail size={16} aria-hidden />
                  {actual.hasPassword ? 'Enviar enlace de contraseña' : 'Reenviar invitación'}
                </button>
              )}
              {actual.status === 'active' ? (
                <button
                  type="button"
                  className="btn-cs btn-peligro"
                  onClick={() => setConfirmarBaja(true)}
                >
                  Desactivar
                </button>
              ) : (
                <button
                  type="button"
                  className="btn-cs btn-exito"
                  onClick={() => cambiarEstado.mutate('active')}
                >
                  Reactivar
                </button>
              )}
            </>
          )
        }
      />

      <div className="grid gap-6 xl:grid-cols-[2fr_3fr]">
        <form
          className="panel-vidrio space-y-4"
          onSubmit={(e) => void form.handleSubmit((v) => guardar.mutate(v))(e)}
          noValidate
        >
          <h2 className="text-lg font-extrabold">Datos</h2>
          <Campo etiqueta="Nombre" error={errors.name?.message}>
            <input className="input-vidrio" disabled={bloqueado} {...form.register('name')} />
          </Campo>
          <Campo etiqueta="Correo" error={errors.email?.message}>
            <input
              className="input-vidrio"
              type="email"
              disabled={bloqueado || actual !== null}
              {...form.register('email')}
            />
          </Campo>
          <Campo
            etiqueta="Página de inicio (opcional)"
            error={errors.startUrl?.message}
            ayuda="A dónde llega al entrar, por ejemplo /alumnos."
          >
            <input
              className="input-vidrio"
              placeholder="/"
              disabled={bloqueado}
              {...form.register('startUrl')}
            />
          </Campo>
          {me?.isSuperadmin && !esYo && (
            <label className="flex items-center gap-2 text-sm font-semibold">
              <input
                type="checkbox"
                className="h-4 w-4 accent-[var(--color-azul-600)]"
                {...form.register('isSuperadmin')}
              />
              Superadmin (acceso total, incluida la gestión de otros superadmins)
            </label>
          )}
          {!bloqueado && (
            <button type="submit" className="btn-cs btn-primario" disabled={guardar.isPending}>
              {guardar.isPending
                ? 'Guardando…'
                : actual
                  ? 'Guardar datos'
                  : 'Crear y enviar invitación'}
            </button>
          )}
        </form>

        <section className="panel-vidrio space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-extrabold">Secciones</h2>
            {actual && !bloqueado && !esYo && (
              <label className="flex items-center gap-2 text-sm">
                <Copy size={16} aria-hidden className="text-texto-3" />
                <select
                  className="input-vidrio py-1.5"
                  value=""
                  onChange={(e) => {
                    const id = Number(e.target.value);
                    if (id) guardarSecciones.mutate({ copyFrom: id });
                  }}
                  aria-label="Copiar secciones de otro staff"
                >
                  <option value="">Copiar de otro staff…</option>
                  {otros.data?.data
                    .filter((o) => o.id !== actual.id)
                    .map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name}
                      </option>
                    ))}
                </select>
              </label>
            )}
          </div>
          {form.watch('isSuperadmin') ? (
            <p className="text-texto-2">
              El superadmin tiene acceso de edición a todas las secciones.
            </p>
          ) : catalogo.data ? (
            <EditorSecciones
              catalogo={catalogo.data}
              valor={secciones}
              onCambio={setSecciones}
              deshabilitado={bloqueado || esYo}
            />
          ) : (
            <Cargando />
          )}
          {esYo && <p className="text-sm text-texto-3">No puedes cambiar tus propias secciones.</p>}
          {actual && !bloqueado && !esYo && !form.watch('isSuperadmin') && (
            <button
              type="button"
              className="btn-cs btn-primario"
              disabled={guardarSecciones.isPending}
              onClick={() => guardarSecciones.mutate({ sections: secciones })}
            >
              {guardarSecciones.isPending ? 'Guardando…' : 'Guardar secciones'}
            </button>
          )}
        </section>
      </div>

      <ConfirmarAccion
        abierto={confirmarBaja}
        onCerrar={() => setConfirmarBaja(false)}
        titulo="Desactivar cuenta"
        descripcion="Se cierran sus sesiones y ya no podrá entrar al panel. Puedes reactivarla después."
        textoBoton="Desactivar"
        peligro
        ocupado={cambiarEstado.isPending}
        onConfirmar={() => cambiarEstado.mutate('disabled')}
      />
      {dialogo}
    </>
  );
}

export function StaffFormPage() {
  const { id } = useParams();
  const numero = id && id !== 'nuevo' ? Number(id) : null;
  const q = useStaff(numero);
  return (
    <PermissionGate seccion="staff" nivel={numero === null ? 'editar' : 'ver'}>
      {numero === null ? (
        <Formulario actual={null} />
      ) : q.isPending ? (
        <Cargando />
      ) : q.isError ? (
        <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />
      ) : (
        <Formulario key={q.data.id} actual={q.data} />
      )}
    </PermissionGate>
  );
}
